import { useState, useEffect, useMemo, useRef } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ChartProps } from '../Interfaces/Charts';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, POPOUT_MESSAGE, COORDINATED_TRANSACTION_SCHEDULING_SUFFIX } from '../Constants';



export default function CoordinatedTransactionScheduling(props: ChartProps) {
    // ***************************************************************************************************************************************************
    // *** Custom Block: the following two defintions are custom to each table this code can produce,
    //      they provide the location for the data and the names of the columns to display
    const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${COORDINATED_TRANSACTION_SCHEDULING_SUFFIX}`;
    const download_filename = 'CoordinatedTransactionScheduling';
    const popout_path = '/charts/coordinatedtransactionscheduling';
    const modalHeader: React.ReactNode = <span>Coordinated Transaction Scheduling</span>;

    interface IRow {
        CASEAPPROVALDATE: Date | string;
        SOLUTIONTIME: Date | string;
        PJMFORECASTEDLMP: number | string;
    }

    function extractRowDataAndIntervalString(json: any) {
        const rowData = json.Interval ?? [];
        const intervalString = json.RefId ?? '';
        return { rowData, intervalString };
    }

    const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
        return (
            <>
                <span>
                    Coordinated Transaction Scheduling (CTS) is a transmission and scheduling product in real-time market which allows market participant to schedule an interchange
                    transaction between MISO and PJM. The below table shows the Price Forecast at the MISO-PJM interface from the CTS case. The Table is updated every 15 minutes.
                </span>
                <br />
                <br /> <span>This table is updated every 5 minutes.</span>
            </>
        );
    }, []);
    // ***************************************************************************************************************************************************

    const [buttonClicked] = [props.buttonClicked];
    const [showModal, setShowModal] = useState(false);
    const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);
    const [rawData, setRawData] = useState('');

    useEffect(() => {
        let downloadBody: React.ReactNode = (
            <div className='download-modal'>
                <Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
                    Download JSON
                </Button>
            </div>
        );

        switch (buttonClicked) {
            case DESCRIPTION_MESSAGE:
                setModalBody(descriptionBody);
                setShowModal(true);
                break;
            case DOWNLOAD_MESSAGE:
                setModalBody(downloadBody);
                setShowModal(true);
                break;
            case POPOUT_MESSAGE:
                const newWinddow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
                if (newWinddow) newWinddow.opener = null;
                break;
            default:
                break;
        }
    }, [buttonClicked, rawData, api_url, descriptionBody]);

    // One-time random refresh interval between 4m50s (290000 ms) and 5m10s (310000 ms)
    const REFRESH_MIN_MS = 290_000;
    const REFRESH_MAX_MS = 310_000;
    const refreshIntervalRef = useRef<number>(
        Math.floor(REFRESH_MIN_MS + Math.random() * (REFRESH_MAX_MS - REFRESH_MIN_MS))
    );

    const useFetchJson = <T,>(url: string) => {
        const [data, setData] = useState<T[]>();
        const [loading, setLoading] = useState(false);
        const [dataInterval, setDataInterval] = useState<string>('');
        const [error, setError] = useState<string>('');

        useEffect(() => {
            let intervalId: NodeJS.Timeout;
            const fetchData = async () => {
                setLoading(true);
                setError('');
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error();
                    const json = await response.json();
                    setRawData(json);
                    const { rowData, intervalString } = extractRowDataAndIntervalString(json);
                    setDataInterval(intervalString);
                    const transformed = rowData.map((r: any) => {
                        const clone: any = { ...r };
                        ['CASEAPPROVALDATE', 'SOLUTIONTIME'].forEach(f => {
                            if (clone[f]) {
                                const d = new Date(clone[f]);
                                if (!isNaN(d.getTime())) clone[f] = d;
                            }
                        });
                        if (clone.PJMFORECASTEDLMP !== undefined && clone.PJMFORECASTEDLMP !== null) {
                            const num = parseFloat(String(clone.PJMFORECASTEDLMP).replace(/,/g, ''));
                            if (!Number.isNaN(num)) clone.PJMFORECASTEDLMP = num;
                        }
                        return clone;
                    });
                    setData(transformed);
                } catch {
                    setData([]);
                    setError('Data Connection Failed');
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
            intervalId = setInterval(fetchData, refreshIntervalRef.current);
            // (Optional) debug: console.debug('CTS refresh every ms:', refreshIntervalRef.current);
            return () => clearInterval(intervalId);
        }, [url]);

        return { data, loading, dataInterval, error };
    };

    const { data, loading, dataInterval, error } = useFetchJson<IRow>(api_url);
    
    // Controlled sorting
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const sortedData = useMemo(
        () => orderBy(data || [], sort),
        [data, sort]
    );

    if (loading) {
        return <div className='loading-and-error-box'>Loading...</div>;
    }
    if (error) {
        return <div className='loading-and-error-box'>{error}</div>;
    }
    if (!data || data.length === 0) {
        return <div className='loading-and-error-box'>No Data Available</div>;
    }

    return (
        <>
            <VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
            <div className='interval-line'>{dataInterval}</div>
            <div className='full-width-grid-wrapper limited-height-grid'>
                <Grid
                    data={sortedData}
                    style={{ width: '100%' }}
                    scrollable="scrollable"
                    resizable
                    sortable
                    sort={sort}
                    onSortChange={e => setSort(e.sort)}
                >
                    <Column field="CASEAPPROVALDATE" title="Case Approval Date" format="{0:MM/dd/yyyy HH:mm}" />
                    <Column field="SOLUTIONTIME" title="Solution Time" format="{0:MM/dd/yyyy HH:mm}" />
                    <Column field="PJMFORECASTEDLMP" title="PJM Forecasted LMP" className="k-text-end" />
                </Grid>
            </div>
        </>
    );
}
